import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ArrowRight, Activity, Brain, Coins, HeartHandshake, Watch } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1477332552946-cfb384aeaf1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const SUPPLEMENT_IMG = "https://images.pexels.com/photos/14029289/pexels-photo-14029289.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CHARITY_IMG = "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=1400&q=80";

function emergentLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

async function whoopLogin() {
  try {
    const r = await api.get("/auth/whoop/authorize");
    window.location.href = r.data.authorize_url;
  } catch (e) {
    toast.error("Couldn't start WHOOP sign-in");
  }
}

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [mode, setMode] = useState("login"); // login | register
  const [impact, setImpact] = useState({ total_donated: 0, donations_count: 0 });
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    if (user) navigate("/app", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/challenges/impact/total").then((r) => setImpact(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (impact.total_donated <= 0) return;
    let start = 0;
    const target = impact.total_donated;
    const duration = 1500;
    const startTime = performance.now();
    const step = (t) => {
      const elapsed = t - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCounter(Math.round(start + (target - start) * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [impact.total_donated]);

  return (
    <div className="bg-white text-obsidian min-h-screen" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="font-display text-2xl tracking-tight" data-testid="landing-logo">FS LAB</div>
          <nav className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
            <a href="#how" className="hover:text-black transition-colors">How it works</a>
            <a href="#impact" className="hover:text-black transition-colors">Impact</a>
            <a href="#stack" className="hover:text-black transition-colors">Stack</a>
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="landing-signin-btn"
              onClick={() => { setMode("login"); setAuthOpen(true); }}
              className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black"
            >
              Sign in
            </button>
            <button
              data-testid="landing-cta-header"
              onClick={() => { setMode("register"); setAuthOpen(true); }}
              className="btn-lime"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 animate-fade-up">
            <div className="eyebrow mb-6">FS LAB — A WEARABLE-FIRST FITNESS LAB</div>
            <h1 className="font-display text-6xl md:text-8xl leading-[0.92] uppercase mb-6">
              Your biometrics<br/>are the boss.<br/>
              <span className="bg-lime px-3 inline-block leading-none">You just listen.</span>
            </h1>
            <p className="text-lg text-neutral-600 max-w-xl mb-10 leading-relaxed">
              Connect your WHOOP. FLYN reads it. Stake real money on 75 Hard — fail a day,
              your money goes to charity. Buy supplements your recovery actually asks for.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <button onClick={() => { setMode("register"); setAuthOpen(true); }} className="btn-lime" data-testid="hero-cta-primary">
                Connect WHOOP <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => { setMode("login"); setAuthOpen(true); }} className="btn-outline-ink" data-testid="hero-cta-secondary">
                I have an account
              </button>
            </div>
            <div className="mt-12 flex items-center gap-8 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
              <div><span className="text-obsidian">7</span> wearables</div>
              <div><span className="text-obsidian">$0</span> to other players</div>
              <div><span className="text-obsidian">100%</span> to charity on loss</div>
            </div>
          </div>

          <div className="md:col-span-6 relative">
            <div className="absolute -inset-10 lime-glow rounded-full pointer-events-none" />
            <div className="relative panel p-8 rounded-3xl overflow-hidden">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="eyebrow">RECOVERY · TODAY</div>
                  <div className="font-display text-6xl mt-2">82<span className="text-3xl">%</span></div>
                </div>
                <div className="w-16 h-16 rounded-full bg-lime flex items-center justify-center">
                  <Activity className="w-7 h-7 text-obsidian" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="eyebrow">HRV</div>
                  <div className="font-display text-3xl mt-1">68<span className="text-base text-neutral-500"> ms</span></div>
                </div>
                <div>
                  <div className="eyebrow">SLEEP</div>
                  <div className="font-display text-3xl mt-1">91<span className="text-base text-neutral-500">/100</span></div>
                </div>
                <div>
                  <div className="eyebrow">STRAIN</div>
                  <div className="font-display text-3xl mt-1">14.2</div>
                </div>
              </div>
              <div className="mt-6 p-4 rounded-2xl bg-obsidian text-white">
                <div className="eyebrow text-lime mb-1">FLYN · LIVE READ</div>
                <p className="text-sm leading-relaxed">
                  Recovery's at 82%, HRV is 68ms — you're primed. Tackle the strain workout for 75 Hard,
                  hydrate hard. Save the second workout for a long walk.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-surface border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="eyebrow mb-3">HOW IT WORKS</div>
          <h2 className="font-display text-4xl md:text-6xl uppercase mb-14 max-w-3xl leading-none">
            One data spine.<br/>Four surfaces. Zero gambling.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: "01",
                icon: Watch,
                title: "Connect your wearable",
                copy: "Sign in with WHOOP — recovery, HRV, sleep, strain sync automatically. Six more wearables land in Phase 2.",
              },
              {
                step: "02",
                icon: Coins,
                title: "Stake the pot",
                copy: "Pick a challenge, set the stake, choose a charity. Money sits in Stripe escrow. Nobody else can win it.",
              },
              {
                step: "03",
                icon: HeartHandshake,
                title: "Survive, verified daily",
                copy: "Workouts auto-check against your wearable. Fail a day — your stake auto-donates to the charity you picked.",
              },
            ].map((s) => (
              <div key={s.step} className="panel p-8 rounded-2xl hover:-translate-y-1 transition-transform">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-xs tracking-[0.25em]">STEP {s.step}</span>
                  <s.icon className="w-6 h-6 text-obsidian" />
                </div>
                <h3 className="font-display text-3xl uppercase mb-4 leading-none">{s.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Charity impact band */}
      <section id="impact" className="relative bg-obsidian text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={CHARITY_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="eyebrow text-lime mb-4">CHARITY · ON · LOSS</div>
              <h2 className="font-display text-5xl md:text-7xl uppercase leading-none mb-6">
                Quitting still<br/>does good.
              </h2>
              <p className="text-neutral-300 text-lg max-w-md leading-relaxed">
                Every failed stake auto-transfers to your chosen charity. No winner-takes-all,
                no pot-splitting, no "win other people's weakness." It's the only legal way to do this,
                and frankly — it's the right way.
              </p>
            </div>
            <div className="text-center md:text-left">
              <div className="eyebrow text-neutral-500 mb-3">DONATED SO FAR</div>
              <div className="font-display text-7xl md:text-9xl text-lime leading-none" data-testid="impact-counter">
                ${counter.toLocaleString()}
              </div>
              <div className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-neutral-400">
                {impact.donations_count.toLocaleString()} donations · across {Object.keys({ givewell:1, wck:1, mind:1, wwf:1 }).length} charities
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supplement teaser */}
      <section id="stack" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="eyebrow mb-3">STACK · BUILT FROM YOUR DATA</div>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-none mb-6">
              Supplements that<br/>read your recovery.
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed mb-8">
              HRV drops below your baseline? FLYN recommends Recover. Sleep latency spiking? Sleep is on
              today's card. No generic stacks — every recommendation cites the exact metric that fired it.
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              {["RECOVER", "SLEEP", "PERFORM", "BASELINE"].map((p) => (
                <div key={p} className="panel rounded-full px-4 py-3 text-center font-mono text-xs tracking-[0.25em]">
                  {p}
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 lime-glow rounded-3xl pointer-events-none opacity-50" />
            <img src={SUPPLEMENT_IMG} alt="Supplement bottles" className="relative rounded-3xl w-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-surface border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 text-center">
          <div className="eyebrow mb-4">READY?</div>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-none mb-8">
            Let your data run<br/>the day.
          </h2>
          <button onClick={() => { setMode("register"); setAuthOpen(true); }} className="btn-lime" data-testid="footer-cta">
            Get Started — Free <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="bg-white py-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-neutral-400">
        FS LAB · BUILT ON YOUR BIOMETRICS · CHARITY-ON-LOSS ONLY
      </footer>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} mode={mode} setMode={setMode} />
    </div>
  );
}

function AuthDialog({ open, onOpenChange, mode, setMode }) {
  const { loginEmail, registerEmail } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        await registerEmail(form.email, form.password, form.name);
      } else {
        await loginEmail(form.email, form.password);
      }
      toast.success(mode === "register" ? "Welcome to FS Lab" : "Welcome back");
      navigate("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-3xl border-neutral-200 p-8 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-4xl uppercase leading-none">
            {mode === "register" ? "Join FS Lab" : "Welcome back"}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs tracking-[0.2em] uppercase text-neutral-500 mt-2">
            {mode === "register" ? "Stake on yourself" : "Pick up where you left off"}
          </DialogDescription>
        </DialogHeader>

        <button
          onClick={emergentLogin}
          data-testid="google-signin-btn"
          className="w-full mt-4 rounded-full border border-neutral-300 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:border-black transition-colors flex items-center justify-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.5-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.4 4 9.8 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.3-2 14-5.4l-6.5-5.5c-2 1.5-4.6 2.4-7.5 2.4-5.4 0-9.7-3.4-11.3-8.1l-6.5 5C9.7 39.7 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.5 5.5C40 35 44 30 44 24c0-1.2-.1-2.5-.4-3.5z"/></svg>
            Continue with Google
        </button>

        <button
          onClick={whoopLogin}
          data-testid="whoop-signin-btn"
          className="w-full mt-2 rounded-full bg-obsidian text-lime px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-black transition-colors flex items-center justify-center gap-2"
        >
          <span className="w-4 h-4 rounded-full bg-lime" /> Continue with WHOOP
        </button>

        <div className="flex items-center gap-3 my-4 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">
          <div className="flex-1 h-px bg-neutral-200" /> OR <div className="flex-1 h-px bg-neutral-200" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Name</Label>
              <Input
                data-testid="auth-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="mt-1 rounded-xl border-neutral-300"
                placeholder="Alex Morgan"
              />
            </div>
          )}
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email</Label>
            <Input
              data-testid="auth-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="mt-1 rounded-xl border-neutral-300"
              placeholder="you@domain.com"
            />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Password</Label>
            <Input
              data-testid="auth-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              className="mt-1 rounded-xl border-neutral-300"
              placeholder="••••••••"
            />
          </div>
          <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full btn-lime mt-2">
            {loading ? "..." : mode === "register" ? "Create account" : "Sign in"}
          </Button>
        </form>

        <div className="text-center mt-4 font-mono text-xs text-neutral-500">
          {mode === "register" ? (
            <>Already a member?{" "}
              <button type="button" onClick={() => setMode("login")} data-testid="switch-to-login" className="text-black underline">Sign in</button>
            </>
          ) : (
            <>New here?{" "}
              <button type="button" onClick={() => setMode("register")} data-testid="switch-to-register" className="text-black underline">Create an account</button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
